import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import OpalCard from '../../../../components/opal/OpalCard'
import Icon from '../../../../components/atoms/Icon'
import { ICON_NAMES } from '../../../../constants'
import DaiTooltip from '../common/DaiTooltip'

const DEFAULT_ICON = ICON_NAMES.cube

const useStyles = makeStyles((theme) => ({
  card: {
    cursor: 'pointer',
    height: '240px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--dai-color-surface3)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-md)'
    }
  },
  cardDisabled: {
    opacity: 0.5,
    cursor: 'default',
    '&:hover': {
      transform: 'none',
      boxShadow: 'none'
    }
  },
  name: {
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 'var(--font-size-md)',
    padding: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    color: 'var(--dai-color-text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  version: {
    textAlign: 'center',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--dai-color-text)',
    fontFamily: 'var(--font-mono)',
    padding: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    flexShrink: 0
  },
  iconContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    minHeight: '120px'
  },
  tooltipWrapper: {
    width: '100%',
    height: '100%',
    cursor: 'pointer'
  }
}))

function SkillCard ({ skill }) {
  const classes = useStyles()
  const content = skill.skillContent || {}

  const isClientOnly = useMemo(() => {
    const focuses = content.focuses || []
    return focuses.length === 1 && focuses[0] === 'client'
  }, [content.focuses])

  const tooltipContent = content.description || skill.skillName

  const handleClick = () => {
    if (isClientOnly) return

    const params = new URLSearchParams()
    params.set('skillId', skill.skillId)
    params.set('skillName', skill.skillName)
    const defaultPrompt = content.defaultPrompt
    if (defaultPrompt) {
      params.set('prompt', defaultPrompt)
    }
    window.open(`/denali-ai/new?${params.toString()}`, '_blank')
  }

  return (
    <DaiTooltip title={tooltipContent} placement='top'>
      <div className={classes.tooltipWrapper} onClick={handleClick}>
        <OpalCard
          className={clsx(classes.card, { [classes.cardDisabled]: isClientOnly })}
        >
          <div className={classes.name}>{skill.skillName}</div>
          <div className={classes.iconContainer}>
            <Icon name={content.iconName || DEFAULT_ICON} customSize='80px' />
          </div>
          <div className={classes.version}>v{skill.version}</div>
        </OpalCard>
      </div>
    </DaiTooltip>
  )
}

SkillCard.propTypes = {
  skill: PropTypes.shape({
    skillId: PropTypes.string.isRequired,
    version: PropTypes.number.isRequired,
    skillName: PropTypes.string.isRequired,
    skillContent: PropTypes.shape({
      enabled: PropTypes.bool,
      focuses: PropTypes.arrayOf(PropTypes.string),
      description: PropTypes.string,
      defaultPrompt: PropTypes.string
    })
  }).isRequired
}

export default SkillCard
